import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { AppOptions, getAllActivityTypes, getActivityTypeDisplayName, getActivityTypeIcon } from '../utils/optionsManager';
import { UnitPreferences } from '../utils/unitConversions';

export type GasDataFilter = 'all' | 'with' | 'without';

export interface DiveFilters {
  minDepth: number | '';
  maxDepth: number | '';
  minDurationMinutes: number | '';
  gasData: GasDataFilter;
}

interface DiveFiltersPanelProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  activityTypeFilters: AppOptions['filters']['activityTypes'];
  onActivityTypeChange: (type: keyof AppOptions['filters']['activityTypes'], checked: boolean) => void;
  filters: DiveFilters;
  onFiltersChange: (filters: DiveFilters) => void;
  unitPreferences: UnitPreferences;
}

const DiveFiltersPanel: React.FC<DiveFiltersPanelProps> = ({
  anchorEl,
  onClose,
  activityTypeFilters,
  onActivityTypeChange,
  filters,
  onFiltersChange,
  unitPreferences,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const depthUnit = unitPreferences.depth === 'feet' ? 'ft' : 'm';

  const handleClear = () => {
    onFiltersChange({ minDepth: '', maxDepth: '', minDurationMinutes: '', gasData: 'all' });
    getAllActivityTypes().forEach((type) => {
      if (!activityTypeFilters[type]) {
        onActivityTypeChange(type, true);
      }
    });
  };

  const hasActiveFilters =
    filters.minDepth !== '' ||
    filters.maxDepth !== '' ||
    filters.minDurationMinutes !== '' ||
    filters.gasData !== 'all' ||
    getAllActivityTypes().some((t) => !activityTypeFilters[t]);

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          width: 320,
          p: 2.5,
          background: isDark
            ? 'linear-gradient(135deg, #1a2733 0%, #1e3347 100%)'
            : 'linear-gradient(135deg, #f0f8ff 0%, #e6f3ff 100%)',
          border: isDark ? '1px solid #42a5f5' : '1px solid #b3d9ff',
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle1" fontWeight="bold">
          🎯 Filter Dives
        </Typography>
        {hasActiveFilters && (
          <Button size="small" onClick={handleClear} color="secondary">
            Clear all
          </Button>
        )}
      </Box>

      {/* Activity Types */}
      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Activity Types
      </Typography>
      <FormGroup sx={{ mt: 0.5, mb: 1.5 }}>
        {getAllActivityTypes().map((activityType) => (
          <FormControlLabel
            key={activityType}
            sx={{ py: 0.25 }}
            control={
              <Checkbox
                size="small"
                checked={activityTypeFilters[activityType]}
                onChange={(e) => onActivityTypeChange(activityType, e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={0.75}>
                <span>{getActivityTypeIcon(activityType)}</span>
                <Typography variant="body2">{getActivityTypeDisplayName(activityType)}</Typography>
              </Box>
            }
          />
        ))}
      </FormGroup>

      <Divider sx={{ my: 1.5 }} />

      {/* Depth Range */}
      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Max Depth Range
      </Typography>
      <Box display="flex" gap={1.5} mt={1} mb={1.5}>
        <TextField
          label="Min"
          type="number"
          size="small"
          value={filters.minDepth}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              minDepth: e.target.value === '' ? '' : Number(e.target.value),
            })
          }
          InputProps={{
            endAdornment: <InputAdornment position="end">{depthUnit}</InputAdornment>,
            inputProps: { min: 0 },
          }}
          sx={{ flex: 1 }}
        />
        <TextField
          label="Max"
          type="number"
          size="small"
          value={filters.maxDepth}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              maxDepth: e.target.value === '' ? '' : Number(e.target.value),
            })
          }
          InputProps={{
            endAdornment: <InputAdornment position="end">{depthUnit}</InputAdornment>,
            inputProps: { min: 0 },
          }}
          sx={{ flex: 1 }}
        />
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Min Duration */}
      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Minimum Dive Time
      </Typography>
      <Box mt={1}>
        <TextField
          label="Min duration"
          type="number"
          size="small"
          fullWidth
          value={filters.minDurationMinutes}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              minDurationMinutes: e.target.value === '' ? '' : Number(e.target.value),
            })
          }
          InputProps={{
            endAdornment: <InputAdornment position="end">min</InputAdornment>,
            inputProps: { min: 0 },
          }}
        />
      </Box>

      <Divider sx={{ my: 1.5 }} />

      {/* Gas Consumption Data */}
      <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Gas Consumption (RMV/SAC)
      </Typography>
      <Box mt={1}>
        <ToggleButtonGroup
          value={filters.gasData}
          exclusive
          fullWidth
          size="small"
          color="primary"
          onChange={(_, value: GasDataFilter | null) => {
            if (value !== null) {
              onFiltersChange({ ...filters, gasData: value });
            }
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="with">With data</ToggleButton>
          <ToggleButton value="without">Without data</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Popover>
  );
};

export const countActiveFilters = (
  activityTypeFilters: AppOptions['filters']['activityTypes'],
  filters: DiveFilters
): number => {
  let count = 0;
  if (filters.minDepth !== '') count++;
  if (filters.maxDepth !== '') count++;
  if (filters.minDurationMinutes !== '') count++;
  if (filters.gasData !== 'all') count++;
  if (getAllActivityTypes().some((t) => !activityTypeFilters[t])) count++;
  return count;
};

export default DiveFiltersPanel;
